import { getContrastRatio } from "@mui/material/styles";
import { neutral } from "@/theme/tokens";

/** Canonical light-mode CTA ink. Same tokens as `neutral.white` / `neutral.black`. */
export const ACCESSIBLE_FOREGROUND_WHITE = neutral.white;
export const ACCESSIBLE_FOREGROUND_DARK = neutral.black;

const MIN_AA = 4.5;

/**
 * Strict WCAG AA foreground for a solid fill, or `null` when neither
 * canonical white nor canonical dark reaches 4.5:1.
 *
 * Prefers white. Does not invent a third ink. Does not change the fill.
 * Never throws.
 */
export function resolveAccessibleForeground(
  background: string,
  minContrast: number = MIN_AA,
): string | null {
  try {
    const vsWhite = getContrastRatio(background, ACCESSIBLE_FOREGROUND_WHITE);
    if (vsWhite >= minContrast) return ACCESSIBLE_FOREGROUND_WHITE;
    const vsBlack = getContrastRatio(background, ACCESSIBLE_FOREGROUND_DARK);
    if (vsBlack >= minContrast) return ACCESSIBLE_FOREGROUND_DARK;
    return null;
  } catch {
    return null;
  }
}

/**
 * Foreground for text/icons on a solid fill.
 * Uses {@link resolveAccessibleForeground}; if both inks miss AA, picks the
 * better of the two so chrome callers always receive a color.
 * Does not change the fill. Never throws.
 *
 * Tenant filled-CTA admission is owned by `resolveTenantActionTokens`
 * (valid hex → branding white; contrast is not an admission gate).
 */
export function accessibleForegroundOn(
  background: string,
  minContrast: number = MIN_AA,
): string {
  const resolved = resolveAccessibleForeground(background, minContrast);
  if (resolved) return resolved;
  try {
    const vsWhite = getContrastRatio(background, ACCESSIBLE_FOREGROUND_WHITE);
    const vsBlack = getContrastRatio(background, ACCESSIBLE_FOREGROUND_DARK);
    return vsWhite >= vsBlack
      ? ACCESSIBLE_FOREGROUND_WHITE
      : ACCESSIBLE_FOREGROUND_DARK;
  } catch {
    return ACCESSIBLE_FOREGROUND_WHITE;
  }
}
